import { emitApiMetricSample } from "./metric-emitter";
import { resolveMatchingApiMonitors } from "./helpers";
import type {
  ApiMonitorDefinition,
  ApiRequestContext,
  MonitoringPrimitive,
  PodMonitoringSchema,
} from "./types";

// XMLHttpRequest instrumentation so axios traffic (the connection-manager default
// browser adapter) records the same API latency and error-rate samples as fetch.
// Stores the original prototype methods so multiple installers share one wrapper.
interface XhrMonitorGlobals {
  __bflMonitoringOriginalOpen__?: typeof XMLHttpRequest.prototype.open;
  __bflMonitoringOriginalSend__?: typeof XMLHttpRequest.prototype.send;
  __bflMonitoringXhrRefCount__?: number;
}

// Captures the metric ids and request attributes needed when an XHR completes.
interface MonitoredApiRequest {
  errorMetricId: string;
  responseTimeMetricId: string;
  attributes: Record<string, MonitoringPrimitive>;
}

// Per-XHR state populated by open() and consumed by send().
interface PendingXhrMonitor {
  requestUrl: string;
  method: string;
  settled: boolean;
}

const pendingMonitors = new WeakMap<XMLHttpRequest, PendingXhrMonitor>();

// Reads the current page context so request metrics can be tied to a screen.
function getRequestPageContext(): Pick<ApiRequestContext, "pagePath" | "pageUrl"> {
  if (typeof window === "undefined") {
    return {
      pagePath: "",
      pageUrl: "",
    };
  }

  return {
    pagePath: window.location.pathname,
    pageUrl: window.location.href,
  };
}

// Builds the classifier input object for the current XHR request.
function createApiRequestContext(requestUrl: string, method: string): ApiRequestContext {
  return {
    url: requestUrl,
    method,
    input: requestUrl,
    init: undefined,
    ...getRequestPageContext(),
  };
}

// Precomputes the metric ids and attributes for one matched API monitor.
function createMonitorSample(
  monitor: ApiMonitorDefinition,
  requestContext: ApiRequestContext
): MonitoredApiRequest {
  const classification = monitor.classifyRequest?.(requestContext);

  return {
    errorMetricId: monitor.errorMetricId,
    responseTimeMetricId: monitor.responseTimeMetricId,
    attributes: {
      api_name: classification?.apiName ?? monitor.apiName,
      request_transport: "xhr",
      request_method: requestContext.method,
      request_page_path: requestContext.pagePath,
      request_page_url: requestContext.pageUrl,
      ...(classification?.attributes ?? {}),
    },
  };
}

// Records a completed XHR using its HTTP status code.
function recordXhrOutcome(
  schema: PodMonitoringSchema,
  monitoredRequests: MonitoredApiRequest[],
  duration: number,
  statusCode: number
): void {
  const success = statusCode >= 200 && statusCode < 300;

  monitoredRequests.forEach((monitor) => {
    emitApiMetricSample(schema, monitor.responseTimeMetricId, {
      value: duration,
      unit: "ms",
      statusCode,
      success,
      percentile: 90,
      extra: {
        ...monitor.attributes,
      },
    });

    emitApiMetricSample(schema, monitor.errorMetricId, {
      value: 1,
      unit: "request",
      statusCode,
      success,
      extra: {
        ...monitor.attributes,
        is_error: !success,
      },
    });
  });
}

// Patches XMLHttpRequest.prototype so matching requests automatically emit API metrics.
export function installXhrMonitoring(schema: PodMonitoringSchema): () => void {
  if (
    typeof window === "undefined" ||
    typeof window.XMLHttpRequest === "undefined" ||
    typeof XMLHttpRequest.prototype.open !== "function" ||
    typeof XMLHttpRequest.prototype.send !== "function"
  ) {
    return () => undefined;
  }

  const globals = window as Window & XhrMonitorGlobals;
  const proto = XMLHttpRequest.prototype;
  const originalOpen = globals.__bflMonitoringOriginalOpen__ ?? proto.open;
  const originalSend = globals.__bflMonitoringOriginalSend__ ?? proto.send;

  if (!globals.__bflMonitoringOriginalOpen__) {
    globals.__bflMonitoringOriginalOpen__ = originalOpen;
    globals.__bflMonitoringOriginalSend__ = originalSend;
    globals.__bflMonitoringXhrRefCount__ = 0;
  }

  globals.__bflMonitoringXhrRefCount__ = (globals.__bflMonitoringXhrRefCount__ ?? 0) + 1;

  proto.open = function (
    this: XMLHttpRequest,
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null
  ) {
    pendingMonitors.set(this, {
      requestUrl: String(url),
      method: String(method).toUpperCase(),
      settled: false,
    });

    return originalOpen.call(this, method, url, async ?? true, username, password);
  } as typeof proto.open;

  proto.send = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
    const pending = pendingMonitors.get(this);

    if (pending && !pending.settled) {
      const matchingMonitors = resolveMatchingApiMonitors(
        schema,
        pending.requestUrl,
        pending.method
      );

      if (matchingMonitors.length > 0) {
        pending.settled = true;

        const requestContext = createApiRequestContext(pending.requestUrl, pending.method);
        const monitoredRequests = matchingMonitors.map((monitor) =>
          createMonitorSample(monitor, requestContext)
        );
        const startTime = performance.now();

        this.addEventListener(
          "loadend",
          (event) => {
            const requestTarget = event.target as XMLHttpRequest;
            recordXhrOutcome(
              schema,
              monitoredRequests,
              performance.now() - startTime,
              requestTarget.status
            );
          },
          { once: true }
        );
      }
    }

    return originalSend.call(this, body);
  } as typeof proto.send;

  return () => {
    const currentRefCount = (globals.__bflMonitoringXhrRefCount__ ?? 1) - 1;
    globals.__bflMonitoringXhrRefCount__ = Math.max(currentRefCount, 0);

    if (globals.__bflMonitoringXhrRefCount__ === 0) {
      if (globals.__bflMonitoringOriginalOpen__) {
        proto.open = globals.__bflMonitoringOriginalOpen__;
      }
      if (globals.__bflMonitoringOriginalSend__) {
        proto.send = globals.__bflMonitoringOriginalSend__;
      }

      delete globals.__bflMonitoringOriginalOpen__;
      delete globals.__bflMonitoringOriginalSend__;
      delete globals.__bflMonitoringXhrRefCount__;
    }
  };
}
